import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DeleteConfirmationModal from '../../Components/Offerings/DeleteConfirmationModal';
import { getOfferings, toggleOfferingStatus, deleteOffering } from '../../api/offerings';
import Header from '../../Components/Header/Header';
import Sidebar from '../../Components/Sidebar/Sidebar';
import '../AdventureShared.css';
import './OfferingsDashboardPage.css';

export default function OfferingsDashboardPage() {
  const navigate = useNavigate();

  // Listings and Filters State
  const [offerings, setOfferings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOption, setSortOption] = useState('newest');

  // Dashboard statistics
  const [stats, setStats] = useState({ total: 0, active: 0, draft: 0 });

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedOffering, setSelectedOffering] = useState(null);

  // Toast state
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);

  const currentUserId = localStorage.getItem("userId") || "";

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      let combined = [];
      if (statusFilter === 'all') {
        const [activeRes, draftRes] = await Promise.all([
          getOfferings({ providerId: currentUserId, search: searchQuery, sort: sortOption, status: 'active' }),
          getOfferings({ providerId: currentUserId, search: searchQuery, sort: sortOption, status: 'draft' })
        ]);
        const activeList = activeRes.data || [];
        const draftList = draftRes.data || [];
        combined = [...activeList, ...draftList];

        if (sortOption === 'oldest') {
          combined.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        } else if (sortOption === 'price_asc') {
          combined.sort((a, b) => (a.price || 0) - (b.price || 0));
        } else if (sortOption === 'price_desc') {
          combined.sort((a, b) => (b.price || 0) - (a.price || 0));
        } else {
          combined.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }
      } else {
        const res = await getOfferings({
          providerId: currentUserId,
          search: searchQuery,
          sort: sortOption,
          status: statusFilter
        });
        combined = res.data || [];
      }

      setOfferings(combined);

      const [activeStats, draftStats] = await Promise.all([
        getOfferings({ providerId: currentUserId, status: 'active' }),
        getOfferings({ providerId: currentUserId, status: 'draft' })
      ]);
      const activeCount = (activeStats.data || []).length;
      const draftCount = (draftStats.data || []).length;
      setStats({ total: activeCount + draftCount, active: activeCount, draft: draftCount });
    } catch (err) {
      console.error("Error loading offerings", err);
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, searchQuery, statusFilter, sortOption]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusToggle = async (offeringId, currentStatus) => {
    if (isTogglingStatus) return;
    const nextStatus = currentStatus === 'active' ? 'draft' : 'active';
    setIsTogglingStatus(true);
    try {
      await toggleOfferingStatus(offeringId, nextStatus);
      showToast(`Status updated to ${nextStatus === 'active' ? 'Active' : 'Draft'}`);
      await fetchData();
    } catch (err) {
      console.error("Failed to toggle status", err);
      showToast("Failed to update status", "error");
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const triggerDelete = (offering) => {
    setSelectedOffering(offering);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedOffering) return;
    try {
      await deleteOffering(selectedOffering.offeringId);
      setDeleteModalOpen(false);
      setSelectedOffering(null);
      showToast("Offering successfully deleted");
      fetchData();
    } catch (err) {
      console.error("Failed to delete offering", err);
      setDeleteModalOpen(false);
      setSelectedOffering(null);
      showToast(err.message || "Failed to delete offering", "error");
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: 'success' }), 5000);
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatPrice = (offering) => {
    const { pricingModel, price, currency } = offering;
    if (pricingModel === 'free') return 'Free';
    if (pricingModel === 'cod') return 'Cash on Delivery';
    const formattedPrice = new Intl.NumberFormat(undefined, {
      style: 'currency', currency: currency || 'USD'
    }).format(price || 0);
    const modelSuffix = pricingModel === 'per_hour' ? '/hr' : pricingModel === 'per_day' ? '/day' : ' flat';
    return `${formattedPrice}${modelSuffix}`;
  };

  return (
    <div className="OfferingsDashboardPage">
      <div className="adventurePageContainer">

        <div className="headerContain">
          <Header />
        </div>

        <div className="adventurePageContainer2">
          <div className="sidebarContain">
            <Sidebar />
          </div>

          <main className="adv-page-main">
             {toast.message && (
               <div 
                 className="off-db-toast" 
                 style={
                   toast.type === 'error' 
                     ? { backgroundColor: '#1c0e0e', borderColor: '#dc3545' } 
                     : {}
                 }
               >
                 <span style={{ color: toast.type === 'error' ? '#dc3545' : '#48d1cc' }}>
                   {toast.type === 'error' ? '⚠️' : '✓'}
                 </span>
                 {toast.message}
               </div>
             )}

            {/* Page Header */}
            <div className="adv-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
              <div>
                <h1 className="adv-page-title">
                  Your <span className="adv-page-title-accent">Offerings</span>
                </h1>
                <p className="adv-page-sub">
                  Manage your service offerings, track performance, and create new listings.
                </p>
              </div>
              <button
                className="off-db-btn-primary"
                onClick={() => navigate('/facilitate/offerings/create')}
              >
                + Create Offering
              </button>
            </div>

            <div className="off-db-grid">
              {/* Left/Middle: List, search and filters */}
              <div className="off-db-main-flow">

                {/* Controls */}
                <div className="off-db-controls">
                  <div className="off-db-search-wrap">
                    <input
                      type="text"
                      className="off-db-search-input"
                      placeholder="Search your offerings..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>

                  <div className="off-db-filter-sort-row">
                    <div className="off-db-tabs">
                      {['all', 'active', 'draft'].map(tab => (
                        <button
                          key={tab}
                          className={`off-db-tab-btn ${statusFilter === tab ? 'active' : ''}`}
                          onClick={() => setStatusFilter(tab)}
                        >
                          {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                      ))}
                    </div>

                    <select
                      className="off-db-sort-dropdown"
                      value={sortOption}
                      onChange={(e) => setSortOption(e.target.value)}
                    >
                      <option value="newest">Newest</option>
                      <option value="oldest">Oldest</option>
                      <option value="price_asc">Price: Low to High</option>
                      <option value="price_desc">Price: High to Low</option>
                    </select>
                  </div>
                </div>

                {/* Offerings list */}
                {isLoading ? (
                  <div className="off-db-loader">
                    <div className="off-db-spinner"></div>
                    <p>Loading offerings...</p>
                  </div>
                ) : offerings.length === 0 ? (
                  <div className="off-db-empty">
                    <div className="off-db-empty__icon">🛍️</div>
                    <h3>No offerings found</h3>
                    <p>You haven't created any offerings matching the current filter.</p>
                    <button
                      className="off-db-btn-primary"
                      onClick={() => navigate('/facilitate/offerings/create')}
                    >
                      Create your first offering
                    </button>
                  </div>
                ) : (
                  <div className="off-db-list">
                    {offerings.map((offering) => (
                      <div
                        key={offering.offeringId}
                        className="off-card animate-fade-in"
                        onClick={() => navigate(`/facilitate/offerings/${offering.offeringId}`)}
                      >
                        <div className="off-card__image-container">
                          <img
                            src={offering.coverImageUrl || "https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&w=400&q=80"}
                            alt={offering.title}
                            className="off-card__img"
                          />
                          <span className={`off-card__status-badge off-card__status-badge--${offering.status}`}>
                            {offering.status}
                          </span>
                          {offering.stockStatus === 'out' && (
                            <span className="off-card__stock-badge off-card__stock-badge--out">
                              🚫 Out of Stock
                            </span>
                          )}
                          {offering.stockStatus === 'low' && (
                            <span className="off-card__stock-badge off-card__stock-badge--low">
                              ⚠️ Low Stock ({offering.minStock})
                            </span>
                          )}
                        </div>

                        <div className="off-card__details">
                          <div className="off-card__meta-top">
                            <span className="off-card__category">{offering.category} • {offering.subcategory}</span>
                            <span className="off-card__date">Added {formatDate(offering.createdAt)}</span>
                          </div>
                          <h3 className="off-card__title">{offering.title}</h3>
                          <p className="off-card__description">{offering.description}</p>
                          <div className="off-card__pricing">{formatPrice(offering)}</div>
                        </div>

                        <div className="off-card__actions" onClick={(e) => e.stopPropagation()}>
                          <div className="off-card__toggle-wrap">
                            <span className="off-card__toggle-label">Status</span>
                            <label className="off-card__switch" style={{ cursor: isTogglingStatus ? 'not-allowed' : 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={offering.status === 'active'}
                                onChange={() => handleStatusToggle(offering.offeringId, offering.status)}
                                disabled={isTogglingStatus}
                              />
                              <span className="off-card__slider" style={{ opacity: isTogglingStatus ? 0.6 : 1 }}></span>
                            </label>
                          </div>

                          <div className="off-card__btn-group">
                            <button
                              className="off-card__btn off-card__btn--edit"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/facilitate/offerings/${offering.offeringId}/edit`);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              className="off-card__btn off-card__btn--delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                triggerDelete(offering);
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Right Widget Panel */}
              <div className="off-db-widget-panel">
                <div className="off-widget">
                  <h3 className="off-widget__title">Marketplace Stats</h3>
                  <div className="off-widget__stats-grid">
                    <div className="off-stat-item">
                      <span className="off-stat-label">Total Offerings</span>
                      <strong className="off-stat-value">{stats.total}</strong>
                    </div>
                    <div className="off-stat-item">
                      <span className="off-stat-label">Active Listings</span>
                      <strong className="off-stat-value off-stat-value--active">{stats.active}</strong>
                    </div>
                    <div className="off-stat-item">
                      <span className="off-stat-label">Draft Listings</span>
                      <strong className="off-stat-value off-stat-value--draft">{stats.draft}</strong>
                    </div>
                  </div>
                </div>

                <div className="off-widget">
                  <h3 className="off-widget__title">Quick Tips</h3>
                  <ul className="off-widget__list">
                    <li>Create detail-rich service descriptions to gain customer trust.</li>
                    <li>Specify clear physical meeting locations or coordinates for physical events.</li>
                    <li>Double check availability windows before activating a listing.</li>
                  </ul>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        offeringTitle={selectedOffering?.title}
        onClose={() => { setDeleteModalOpen(false); setSelectedOffering(null); }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
